import { differenceInCalendarDays } from 'date-fns'

import { BlueprintLiveEvent } from '../../../../api/core'

// Derived view-model for the structured event table under an expanded
// game-level blueprint (Live Event Analytics drill-down, level 3).

export type LiveEventStatus = 'active' | 'upcoming' | 'ended'

export function liveEventStatus(event: Pick<BlueprintLiveEvent, 'startDate' | 'endDate'>, now: number): LiveEventStatus {
  if (event.startDate > now) return 'upcoming'
  if (event.endDate >= now) return 'active'
  return 'ended'
}

// Gaps longer than a week break the cadence and are flagged in the table.
export const GAP_WARNING_THRESHOLD_DAYS = 7

export type BlueprintEventRow = {
  // Stable identity for virtualization/memoization; falls back to start+index
  // for older payloads without an event id.
  key: string
  event: BlueprintLiveEvent
  // Event name without the redundant "<blueprint>:" prefix (the blueprint name
  // is already in the header). Empty when the payload has no name.
  name: string
  startDate: number
  endDate: number
  // Calendar year the event started in — drives the per-year filter pills.
  year: number
  // Inclusive calendar-day length (Aug 26 → Sep 8 = 14 d).
  durationDays: number
  // Idle days before this event started: days between the latest end among all
  // earlier-starting events and this event's start (an event starting the day
  // after that end = 0). An event that starts while an earlier one is still
  // running has no gap (0), even if the event right before it ended long ago.
  // `null` for the oldest event.
  gapBeforeDays: number | null
  // Name of the event `gapBeforeDays` is measured from (the latest-ending earlier event).
  // Resolved while the rows are built, in the same order the gap is, so events sharing a
  // start date can't swap places afterwards. `null` for the oldest event.
  gapFromName: string | null
  status: LiveEventStatus
  // True when the BE flags this event as a reskin of an earlier one in the blueprint.
  isReskin: boolean
  // True on the first instance of an event the BE flags as a mechanic change (`MECHANIC_CHANGE` in `types`),
  // the same rule as the Event dialog's Blueprint tab. The BE flags the event, so every instance carries it;
  // later instances use the updated mechanic without introducing it.
  isMechanicChange: boolean
  // Full (unstripped) name of the chronologically previous event — what a reskin
  // re-dresses. `null` for the oldest event.
  previousEventName: string | null
}

export type BlueprintEventFilter = 'all' | number

/**
 * Drop the "<blueprint name>:" prefix analysts put on event names, since the
 * table already sits under the blueprint header. Case-insensitive; also
 * accepts an en/em dash or a hyphen as the separator.
 */
export function stripBlueprintPrefix(name: string | undefined, blueprintName: string | undefined): string {
  const trimmed = (name ?? '').trim()
  const prefix = (blueprintName ?? '').trim()
  if (!trimmed || !prefix) return trimmed
  if (!trimmed.toLowerCase().startsWith(prefix.toLowerCase())) return trimmed
  const rest = trimmed.slice(prefix.length)
  const separator = rest.match(/^\s*[:\-–—]\s*/)
  if (!separator) return trimmed
  const stripped = rest.slice(separator[0].length).trim()
  return stripped || trimmed
}

export function eventDurationDays(event: Pick<BlueprintLiveEvent, 'startDate' | 'endDate'>): number {
  return Math.max(1, differenceInCalendarDays(event.endDate, event.startDate) + 1)
}

/**
 * Build the table rows from the raw blueprint events. Rows are returned
 * newest-first (the table's default sort) but gaps are computed on the
 * chronological sequence so they never depend on the active sort or filter.
 *
 * Events that have not started yet are left out entirely, matching the live
 * events calendar: the adopters table is a record of what games have actually
 * run, and analysts bulk-schedule instances ahead of time, so anything dated
 * forward is a plan rather than an observation. The newest row is therefore
 * always an event that started today or earlier, and no row can be `upcoming`.
 */
export function buildBlueprintEventRows(events: BlueprintLiveEvent[] | undefined, blueprintName: string | undefined, now: number): BlueprintEventRow[] {
  // The first instance of an event is its earliest start, taken before scheduled instances are left out below.
  // Two starts of one event on the same date are told apart by the earlier end, then by their order in the payload,
  // so exactly one of them is the first instance.
  const firstInstanceByEvent = new Map<string, BlueprintLiveEvent>()
  ;(events ?? []).forEach((event) => {
    if (!event.eventId) return
    const current = firstInstanceByEvent.get(event.eventId)
    if (!current || event.startDate < current.startDate || (event.startDate === current.startDate && event.endDate < current.endDate)) {
      firstInstanceByEvent.set(event.eventId, event)
    }
  })
  const chronological = [...(events ?? [])].filter((event) => event.startDate <= now).sort((a, b) => a.startDate - b.startDate || a.endDate - b.endDate)
  let maxEnd: number | null = null
  // The earlier event that ends last; on equal ends the later one wins.
  let latestEndingName: string | null = null
  const rows = chronological.map((event, index): BlueprintEventRow => {
    const previous = index > 0 ? chronological[index - 1] : undefined
    const gapBeforeDays = maxEnd !== null ? Math.max(0, differenceInCalendarDays(event.startDate, maxEnd) - 1) : null
    const gapFromName = latestEndingName
    const name = stripBlueprintPrefix(event.name, blueprintName)
    if (maxEnd === null || event.endDate >= maxEnd) {
      maxEnd = event.endDate
      latestEndingName = name
    }
    return {
      key: event.eventId ? `${event.eventId}-${event.startDate}` : `${event.startDate}-${index}`,
      event,
      name,
      startDate: event.startDate,
      endDate: event.endDate,
      year: new Date(event.startDate).getFullYear(),
      durationDays: eventDurationDays(event),
      gapBeforeDays,
      gapFromName,
      status: liveEventStatus(event, now),
      isReskin: Boolean(event.types?.includes('RESKIN')),
      // Without an `eventId` instances can't be grouped, so the BE flag is kept as it is rather than guessed at.
      isMechanicChange: Boolean(event.types?.includes('MECHANIC_CHANGE')) && (!event.eventId || firstInstanceByEvent.get(event.eventId) === event),
      previousEventName: previous ? previous.name?.trim() || null : null,
    }
  })
  return rows.reverse()
}

/** Distinct start years present in the rows, newest first. */
export function blueprintEventYears(rows: BlueprintEventRow[]): number[] {
  return Array.from(new Set(rows.map((row) => row.year))).sort((a, b) => b - a)
}

export function filterBlueprintEventRows(rows: BlueprintEventRow[], filter: BlueprintEventFilter): BlueprintEventRow[] {
  if (filter === 'all') return rows
  return rows.filter((row) => row.year === filter)
}

export type BlueprintEventSortKey = 'startDate' | 'endDate' | 'durationDays' | 'gapBeforeDays' | 'status'
export type BlueprintEventSort = { key: BlueprintEventSortKey; order: 'asc' | 'desc' }

export const DEFAULT_BLUEPRINT_EVENT_SORT: BlueprintEventSort = { key: 'startDate', order: 'desc' }

const STATUS_SORT_RANK: Record<LiveEventStatus, number> = { active: 0, upcoming: 1, ended: 2 }

function sortValue(row: BlueprintEventRow, key: BlueprintEventSortKey): number {
  if (key === 'status') return STATUS_SORT_RANK[row.status]
  if (key === 'gapBeforeDays') return row.gapBeforeDays ?? -1
  return row[key]
}

/** Stable sort by one column; ties fall back to newest start first. */
export function sortBlueprintEventRows(rows: BlueprintEventRow[], sort: BlueprintEventSort): BlueprintEventRow[] {
  const direction = sort.order === 'asc' ? 1 : -1
  return [...rows].sort((a, b) => {
    const diff = sortValue(a, sort.key) - sortValue(b, sort.key)
    return diff !== 0 ? diff * direction : b.startDate - a.startDate
  })
}

/** Toggle direction when the same column is clicked again; otherwise start desc. */
export function nextBlueprintEventSort(current: BlueprintEventSort, key: BlueprintEventSortKey): BlueprintEventSort {
  if (current.key !== key) return { key, order: 'desc' }
  return { key, order: current.order === 'desc' ? 'asc' : 'desc' }
}

/**
 * Years a family's game-level blueprints span, newest first, from the list
 * payload's `earliestEvent` / `latestEvent` — available before any events are
 * loaded, so the year pills can render up front. Implausible timestamps
 * (missing / epoch defaults) are ignored.
 *
 * Capped at the current year: `latestEvent` is the latest end, scheduled instances included, and
 * the table never shows an event that hasn't started, so a pill for next year
 * could only ever come up empty.
 */
export function blueprintYearRange(items: { earliestEvent?: number; latestEvent?: number }[], now = Date.now()): number[] {
  const currentYear = new Date(now).getFullYear()
  const maxYear = currentYear + 1
  let first = Infinity
  let last = -Infinity
  items.forEach((item) => {
    const from = item.earliestEvent ? new Date(item.earliestEvent).getFullYear() : undefined
    const to = item.latestEvent ? new Date(item.latestEvent).getFullYear() : undefined
    if (from !== undefined && from >= 2000 && from <= maxYear) first = Math.min(first, from)
    if (to !== undefined && to >= 2000 && to <= maxYear) last = Math.max(last, to)
  })
  if (!Number.isFinite(first) && !Number.isFinite(last)) return []
  const start = Number.isFinite(first) ? first : last
  // Everything on record is still ahead of us: no year has anything to show.
  if (start > currentYear) return []
  const end = Math.min(Number.isFinite(last) ? Math.max(last, start) : start, currentYear)
  const years: number[] = []
  for (let year = end; year >= start; year -= 1) years.push(year)
  return years
}

/**
 * Whether a game-level blueprint could hold a row matching the filter, judged
 * from the list payload's event span alone — i.e. before its events have been
 * fetched. Lets the adopters table drop games the filter excludes instead of
 * listing them with an unfiltered count and revealing "no matching events" only
 * once the user expands them.
 *
 * Returns false only when the span *proves* there is no match: either every
 * event is still scheduled (`buildBlueprintEventRows` drops those) or the picked
 * year falls outside the span entirely. The year pills bucket events by start
 * date, and every event's start falls within `[earliestEvent, latestEvent]`
 * (earliest start, latest end), so a year outside that range cannot contain
 * one. Implausible timestamps are treated as unknown rather than as an
 * exclusion, matching `blueprintYearRange`.
 */
export function blueprintSpanMatchesFilter(
  item: { earliestEvent?: number; latestEvent?: number; liveEventsCount?: number },
  filter: BlueprintEventFilter,
  now = Date.now()
): boolean {
  // Nothing has started yet, so no filter — not even "All" — has a row to show for it.
  if (item.earliestEvent && item.earliestEvent > now) return false

  if (filter === 'all') {
    // Adopters with no tracked instances on record — nothing to expand into.
    if (item.liveEventsCount === 0) return false
    return true
  }

  const maxYear = new Date(now).getFullYear() + 1
  const plausibleYear = (timestamp: number | undefined): number | undefined => {
    if (!timestamp) return undefined
    const year = new Date(timestamp).getFullYear()
    return year >= 2000 && year <= maxYear ? year : undefined
  }
  const first = plausibleYear(item.earliestEvent)
  const last = plausibleYear(item.latestEvent)
  if (first !== undefined && filter < first) return false
  if (last !== undefined && filter > last) return false
  return true
}

export type BlueprintEventGroup = {
  // Every row of one game-level blueprint (gaps are chronological within it).
  allRows: BlueprintEventRow[]
  // The subset that passes the active filter.
  visibleRows: BlueprintEventRow[]
}

// 'error': no loaded blueprint matches, but a failed request leaves the answer open.
export type GameFilterMatchState = 'pending' | 'match' | 'no-match' | 'error'

/**
 * Whether an adopting game should stay in the list under a year filter once
 * its blueprint details are available. The list payload's span can cover a year
 * via scheduled instances while no started event actually falls in it, so the
 * table prefetches events and applies the same row filter here. A failed
 * request is never read as "no events", or the game would silently vanish.
 */
export function gameGroupFilterMatchState(
  blueprints: { id: string; names?: { [key: string]: string | undefined } }[],
  detailByBlueprintId: ReadonlyMap<string, { data?: { liveEvents?: BlueprintLiveEvent[] }; isLoading: boolean; isError?: boolean }>,
  filter: BlueprintEventFilter,
  blueprintDisplayName: (names?: { [key: string]: string | undefined }) => string,
  now: number
): GameFilterMatchState {
  if (filter === 'all') return 'match'

  let anyPending = false
  let anyFailed = false
  for (const item of blueprints) {
    const detail = detailByBlueprintId.get(item.id)
    if (!detail || detail.isLoading) {
      anyPending = true
      continue
    }
    if (detail.isError) {
      anyFailed = true
      continue
    }
    const rows = buildBlueprintEventRows(detail.data?.liveEvents, blueprintDisplayName(item.names), now)
    if (filterBlueprintEventRows(rows, filter).length > 0) return 'match'
  }
  if (anyPending) return 'pending'
  return anyFailed ? 'error' : 'no-match'
}

type LongestBreak = { days: number; fromName: string; toName: string }

/**
 * The row whose preceding gap is the longest. Ties go to the most recent pair
 * (latest start, then latest end, then key) so the answer depends only on the
 * rows themselves, never on the order they arrive in — the table's active sort
 * must not change which break is reported.
 */
function pickLongestBreakRow(rows: BlueprintEventRow[]): (BlueprintEventRow & { gapBeforeDays: number }) | null {
  let best: (BlueprintEventRow & { gapBeforeDays: number }) | null = null
  for (const row of rows) {
    if (row.gapBeforeDays === null) continue
    const candidate = row as BlueprintEventRow & { gapBeforeDays: number }
    if (!best || isLongerBreak(candidate, best)) best = candidate
  }
  return best
}

function isLongerBreak(a: BlueprintEventRow & { gapBeforeDays: number }, b: BlueprintEventRow & { gapBeforeDays: number }): boolean {
  if (a.gapBeforeDays !== b.gapBeforeDays) return a.gapBeforeDays > b.gapBeforeDays
  if (a.startDate !== b.startDate) return a.startDate > b.startDate
  if (a.endDate !== b.endDate) return a.endDate > b.endDate
  return a.key > b.key
}

function toLongestBreak(row: BlueprintEventRow & { gapBeforeDays: number }): LongestBreak {
  return { days: row.gapBeforeDays, fromName: row.gapFromName ?? '', toName: row.name }
}

export type GameEventsAggregate = {
  visibleCount: number
  liveCount: number
  avgDurationDays: number | null
  avgGapDays: number | null
  longestBreak: { days: number; fromName: string; toName: string } | null
}

/**
 * Game-row aggregates across a game's blueprints. Averages and the live count
 * cover the visible (filtered) rows; the longest break is resolved per
 * blueprint so the "previous event" is never borrowed from a sibling blueprint.
 */
export function aggregateGameEvents(groups: BlueprintEventGroup[]): GameEventsAggregate {
  const visible = groups.flatMap((group) => group.visibleRows)
  const visibleCount = visible.length
  const liveCount = visible.filter((row) => row.status === 'active').length
  const avgDurationDays = visibleCount > 0 ? Math.round(visible.reduce((sum, row) => sum + row.durationDays, 0) / visibleCount) : null
  const gaps = visible.map((row) => row.gapBeforeDays).filter((gap): gap is number => gap !== null)
  const avgGapDays = gaps.length > 0 ? Math.round(gaps.reduce((sum, gap) => sum + gap, 0) / gaps.length) : null
  const longestBreakRow = pickLongestBreakRow(groups.flatMap((group) => group.visibleRows))
  const longestBreak = longestBreakRow ? toLongestBreak(longestBreakRow) : null
  return { visibleCount, liveCount, avgDurationDays, avgGapDays, longestBreak }
}

/**
 * When a game first ran the family within what the table is showing: the
 * earliest start across the visible (year-filtered) rows of every blueprint, so
 * under a year it is the first month of that year the game ran an event, not
 * its all-time first. Blueprints that failed or haven't loaded contribute
 * nothing; `undefined` when no visible row exists. Read from the rows rather
 * than the list payload's `earliestEvent`, so the label always agrees with the
 * events shown beneath it.
 *
 * Only the label is year-scoped: the Mechanic Change flag on each row keeps its
 * all-time first-instance rule (see `buildBlueprintEventRows`).
 */
export function earliestVisibleRowStart(groups: Pick<BlueprintEventGroup, 'visibleRows'>[]): number | undefined {
  let earliest: number | undefined
  groups.forEach((group) =>
    group.visibleRows.forEach((row) => {
      if (earliest === undefined || row.startDate < earliest) earliest = row.startDate
    })
  )
  return earliest
}

export type BlueprintEventsSummary = {
  count: number
  // Rounded to whole days; `null` when there is nothing to average.
  avgDurationDays: number | null
  avgGapDays: number | null
  longestBreak: { days: number; fromName: string; toName: string } | null
}

/**
 * Footer figures for the visible rows. The longest break names the two
 * events around the gap: the event whose end the gap is measured from (the
 * latest-ending of the earlier events, matching `gapBeforeDays`) → this event.
 * The earlier event's name rides on the row (`gapFromName`), so it resolves even
 * when that event is filtered out of `rows`.
 */
export function summarizeBlueprintEventRows(rows: BlueprintEventRow[]): BlueprintEventsSummary {
  const count = rows.length
  const avgDurationDays = count > 0 ? Math.round(rows.reduce((sum, row) => sum + row.durationDays, 0) / count) : null
  const withGap = rows.filter((row): row is BlueprintEventRow & { gapBeforeDays: number } => row.gapBeforeDays !== null)
  const avgGapDays = withGap.length > 0 ? Math.round(withGap.reduce((sum, row) => sum + row.gapBeforeDays, 0) / withGap.length) : null

  const longest = pickLongestBreakRow(rows)
  const longestBreak = longest ? toLongestBreak(longest) : null

  return { count, avgDurationDays, avgGapDays, longestBreak }
}
